import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, useState, useRef } from 'react'
import { DateRange } from 'react-date-range'
import { formatDateYMD } from '../../utils/dateFormatter'
import useOutsideClick from '../../utils/useOutsideClick'
import './DateRangePicker.scss'

const DateRangePicker = ({
  state,
  setState,
  size,
  width = '100%',
  disabled = false,
  bonus = false,
  transaction = false,
  player = false,
  isMAXwidth= false
}) => {
  const { ref, isVisible, setIsVisible } = useOutsideClick(false)
  const containerRef = useRef(null)
  const [calendarPosition, setCalendarPosition] = useState({
    top: '100%',
    left: '0',
    right: 'auto',
    bottom: 'auto',
    transform: 'none'
  })

  // Calculate and update calendar position when visibility changes
  useEffect(() => {
    if (isVisible && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const viewportWidth = window.innerWidth

      // Calendar dimensions (approximate)
      const calendarHeight = 350
      const calendarWidth = 650

      // Available space calculations
      const spaceBelow = viewportHeight - rect.bottom
      const spaceAbove = rect.top
      const spaceRight = viewportWidth - rect.left

      // Initialize position object
      let newPosition = {
        top: '100%',
        left: '0',
        right: 'auto',
        bottom: 'auto',
        transform: 'none'
      }

      // Vertical positioning
      if (spaceBelow < calendarHeight && spaceAbove > spaceBelow) {
        newPosition.top = 'auto'
        newPosition.bottom = '100%'
      }

      // Horizontal positioning - handle both left and right constraints
      if (spaceRight < calendarWidth) {
        // Not enough space on the right, adjust position
        const rightOffset = Math.min(calendarWidth - spaceRight, rect.left)
        if (rightOffset > 0) {
          newPosition.transform = `translateX(-${100}px)`
        }
      }

      // Special adjustment for medium screens (768px to 1285px)
      if (viewportWidth >= 768 && viewportWidth <= 1285) {
        // Shift to the right by 100px, but ensure it doesn't go off-screen
        const rightShift = 100
        const maxShift = Math.max(0, spaceRight - calendarWidth)
        const actualShift = Math.min(rightShift, maxShift)

        if (actualShift > 0) {
          newPosition.left = `${actualShift}px`
        }
      }

      // For smaller screens, ensure there's enough space on the left
      if (viewportWidth < 768) {
        // On small screens, prioritize visibility over exact positioning
        // If calendar would be cut off on the left, adjust position
        if (rect.left < 20) { // 20px minimum margin from left edge
          newPosition.left = '0'
          newPosition.transform = 'none'
        }

        // If calendar is wider than viewport, scale it down
        if (calendarWidth > viewportWidth - 40) { // 20px margin on each side
          newPosition.maxWidth = `${viewportWidth - 40}px`
          newPosition.transform = 'none'
        }
      }

      setCalendarPosition(newPosition)
    }
  }, [isVisible])
  return (
    <div
      className='custom-container'
      style={{ marginTop: (bonus || player) && '0px', position: 'relative' ,  width: isMAXwidth ? '100%' : undefined}}
      ref={containerRef}
    >
      <input
        readOnly
        disabled={disabled}
        className={
          size ? `form-control form-control-${size} ${player ? 'player' : ''}` : `form-control ${player ? 'player' : ''}`
        }
        onClick={() => setIsVisible(!isVisible)}
        value={
          state?.[0]?.startDate && state?.[0]?.endDate
            ? `${formatDateYMD(state[0].startDate)} to ${formatDateYMD(state[0].endDate)}`
            : 'Select Date Range'
        }
        // value={`${formatDateYMD(state[0]?.startDate)} to ${formatDateYMD(state[0]?.endDate)}`}
      />

      {isVisible && (
        <div
          ref={ref}
          className='calendar-view'
          style={{
            position: 'absolute',
            zIndex: 1000,
            top: calendarPosition.top,
            left: calendarPosition.left,
            right: calendarPosition.right,
            bottom: calendarPosition.bottom,
            transform: calendarPosition.transform,
            maxWidth: calendarPosition.maxWidth || '100vw',
            overflow: 'auto'
          }}
        >
          {player
            ? (
              <DateRange
                editableDateInputs
                onChange={(item) => {
                  setState([{ ...item.selection, key: 'selection' }])
                }}
                // moveRangeOnFirstSelection={false}
                // ranges={state}
                // maxDate={new Date()}
                moveRangeOnFirstSelection={false}
                ranges={[
                  {
                    startDate: state?.[0]?.startDate || new Date(), // default if undefined
                    endDate: state?.[0]?.endDate || new Date(),
                    key: 'selection'
                  }
                ]}
                maxDate={new Date()}
                initialFocusedRange={null} 
              />
            )
            : (
              <DateRange
                editableDateInputs
                onChange={(item) => {
                  setState([item.selection])
                }}
                moveRangeOnFirstSelection={false}
                ranges={state}
                minDate={bonus ? (transaction ? new Date(1999) : new Date()) : new Date(1999)}
                maxDate={bonus ? undefined : new Date()}
              />
            )}
        </div>
      )}
    </div>
  )
}

export default DateRangePicker

export const DateRangePickerWithoutInput = ({ state, setState }) => {
  const { ref, isVisible, setIsVisible } = useOutsideClick(false)
  const containerRef = useRef(null)
  const [calendarPosition, setCalendarPosition] = useState({
    top: '40px',
    right: '0px',
    left: 'auto',
    bottom: 'auto',
    transform: 'none'
  })

  // Calculate and update calendar position when visibility changes
  useEffect(() => {
    if (isVisible && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const viewportWidth = window.innerWidth

      // Calendar dimensions (approximate)
      const calendarHeight = 350
      const calendarWidth = 650

      // Available space calculations
      const spaceBelow = viewportHeight - rect.bottom
      const spaceRight = viewportWidth - rect.right

      // Initialize position object
      let newPosition = {
        top: '40px',
        right: '0px',
        left: 'auto',
        bottom: 'auto',
        transform: 'none'
      }

      // Vertical positioning
      if (spaceBelow < calendarHeight) {
        newPosition.top = 'auto'
        newPosition.bottom = '40px'
      }

      // Horizontal positioning - only adjust if needed
      if (spaceRight < calendarWidth) {
        // Not enough space on the right
        if (rect.right - calendarWidth < 0) {
          // Calendar would go off the left edge of screen
          newPosition.right = 'auto'
          newPosition.left = '0'
        } else {
          // Keep right-aligned but adjust if needed
          newPosition.right = `${Math.max(0, calendarWidth - spaceRight)}px`
        }
      }

      // Special adjustment for medium screens (768px to 1285px)
      if (viewportWidth >= 768 && viewportWidth <= 1285) {
        // For the second component, we want to ensure it stays right-aligned
        // but with a slight adjustment to prevent it from being cut off
        const rightShift = 50; // Less shift for this component
        newPosition.right = `${rightShift}px`;
      }

      // For smaller screens, ensure there's enough space
      if (viewportWidth < 768) {
        // If calendar is wider than viewport, scale it down
        if (calendarWidth > viewportWidth - 40) { // 20px margin on each side
          newPosition.maxWidth = `${viewportWidth - 40}px`
        }
      }

      setCalendarPosition(newPosition)
    }
  }, [isVisible])

  return (
    <div
      className='custom-container date d-flex align-items-center mt-0'
      ref={containerRef}
      style={{ position: 'relative' }}
    >
      <span
        className='d-flex '
        style={{ cursor: 'pointer' }}
        onClick={() => setIsVisible(!isVisible)}
      >
        {formatDateYMD(state?.[0]?.startDate)} - {formatDateYMD(state?.[0]?.endDate)}&nbsp;  PERIOD &nbsp; <FontAwesomeIcon icon={faCalendarAlt} className='' />{' '} &nbsp;
      </span>

      {isVisible && (
        <div
          ref={ref}
          style={{
            zIndex: '9999',
            position: 'absolute',
            top: calendarPosition.top,
            right: calendarPosition.right,
            left: calendarPosition.left,
            bottom: calendarPosition.bottom,
            transform: calendarPosition.transform,
            maxWidth: calendarPosition.maxWidth || '100vw',
            overflow: 'auto'
          }}
        >
          <DateRange
            editableDateInputs
            onChange={(item) => {
              setState([item.selection])
            }}
            moveRangeOnFirstSelection={false}
            ranges={state}
          />
        </div>
      )}
    </div>
  )
}
